import AccordionButton from '@/components/accordion-button';
import { SdPosition } from '@/lib/models/user-event';
import Text from '@/components/text';
import { FC, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import Tooltip from '@/components/tooltip';
import { Stack, Theme } from '@mui/material';
import { SensitiveDataEntityConfig } from '@/lib/models/policy-model-v1/sensitive-data';
import classNames from 'classnames';

interface Props {
  originalMessage: string;
  detections: SdPosition[];
}

type TextSegment = {
  text: string;
  detection: SdPosition | null;
};

const useStyles = createUseStyles((theme: Theme) => ({
  accordionButton: {
    '& .MuiAccordion-root': {
      border: 'none',
      backgroundColor: theme.palette.subtleGray,
    },
  },
  originalMessage: {
    whiteSpace: 'pre-wrap',
  },
  detectionHighlight: {
    borderRadius: theme.shape.borderRadius,
    padding: '1px 2px',
    margin: 1,
    backgroundColor: `${theme.palette.error.main}20`,
    border: `1px solid ${theme.palette.error.main}`,
    cursor: 'pointer',
    display: 'inline',
  },
}));

const getSDTypeLabel = (type: string) => {
  return (
    SensitiveDataEntityConfig.find(({ entityType }) => entityType === type)?.name ||
    type.replaceAll('_', ' ')
  );
};

const OriginalMessage: FC<Props> = ({ originalMessage, detections }) => {
  const styles = useStyles();
  const textSegments = useMemo(() => {
    // Flatten the detections array and sort by startPos
    const flatDetections = detections.flat().sort((a, b) => a.startPos - b.startPos);
    if (!flatDetections.length) {
      return [{ text: originalMessage, detection: null }];
    }

    const segments: TextSegment[] = [];
    let currentIndex = 0;

    flatDetections.forEach((detection) => {
      const { startPos, endPos } = detection;

      // Add text before detection
      if (currentIndex < startPos) {
        segments.push({
          text: originalMessage.slice(currentIndex, startPos),
          detection: null,
        });
      }

      // Add detection text
      if (startPos < originalMessage.length) {
        const detectionText = originalMessage.slice(
          startPos,
          Math.min(endPos, originalMessage.length)
        );
        segments.push({
          text: detectionText,
          detection,
        });
      }

      currentIndex = Math.max(currentIndex, endPos);
    });

    // Add remaining text after last detection
    if (currentIndex < originalMessage.length) {
      segments.push({
        text: originalMessage.slice(currentIndex),
        detection: null,
      });
    }

    return segments;
  }, [originalMessage, detections]);

  const compClass = classNames(styles.originalMessage, 'dd-privacy-mask');

  return (
    <AccordionButton
      title={
        <Stack direction="row" gap={0.5}>
          <Text size="small" bold>
            Original Message
          </Text>
        </Stack>
      }
      className={styles.accordionButton}
    >
      <Text size="small" className={compClass}>
        {textSegments.map((segment, index) => {
          if (segment.detection) {
            const type = getSDTypeLabel(segment.detection.type);
            return (
              <Tooltip key={index} title={type} placement="top">
                <span className={styles.detectionHighlight}>{segment.text}</span>
              </Tooltip>
            );
          }
          return <span key={index}>{segment.text}</span>;
        })}
      </Text>
    </AccordionButton>
  );
};
export default OriginalMessage;
